import React from "react";
import {
  alpha,
  Box,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LocationOnIcon from "@mui/icons-material/LocationOn";

type KeyFactProps = {
  icon: React.ReactNode;
  label: string;
  value: string;
};

const KeyFact: React.FC<KeyFactProps> = ({ icon, label, value }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accentIcon =
    theme.palette.mode === "dark"
      ? theme.palette.secondary.light
      : theme.palette.primary.main;
  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="flex-start"
      sx={{
        py: { xs: 1.5, sm: 1.35 },
        px: { xs: 1.75, sm: 1.5 },
        borderRadius: 2,
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        bgcolor: alpha(
          theme.palette.primary.main,
          theme.palette.mode === "dark" ? 0.1 : 0.06
        ),
      }}
    >
      <Box
        sx={{
          color: accentIcon,
          display: "flex",
          mt: 0.25,
          "& svg": { fontSize: 28 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: isDark ? "grey.100" : "text.secondary",
            textTransform: "uppercase",
            mb: 0.35,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            lineHeight: 1.35,
            color: "text.primary",
            fontSize: { xs: "0.98rem", sm: "1.02rem" },
          }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  );
};

export type HomeKeyFactsSectionProps = {
  lieuLine: string;
  weekendLine: string;
  dotationsLine: string;
};

/**
 * Bloc commun « Compétition / Lieu / Dotations » (home pré-ouverture et inscriptions ouvertes).
 */
export const HomeKeyFactsSection: React.FC<HomeKeyFactsSectionProps> = ({
  lieuLine,
  weekendLine,
  dotationsLine,
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        py: { xs: 2.25, sm: 2.75 },
        px: { xs: 0.25, sm: 1.25 },
        mx: { xs: -0.25, sm: 0 },
        borderTop: 1,
        borderBottom: 1,
        borderColor: alpha(theme.palette.divider, 0.85),
      }}
    >
      {/* Empilé : la carte hero (~680px) ne laisse pas assez de place pour 3 colonnes lisibles. */}
      <Grid container spacing={{ xs: 2, sm: 2.5 }}>
        <Grid item xs={12}>
          <KeyFact
            icon={<CalendarMonthIcon aria-hidden />}
            label="Compétition"
            value={weekendLine}
          />
        </Grid>
        <Grid item xs={12}>
          <KeyFact
            icon={<LocationOnIcon aria-hidden />}
            label="Lieu"
            value={lieuLine}
          />
        </Grid>
        <Grid item xs={12}>
          <KeyFact
            icon={<EmojiEventsIcon aria-hidden />}
            label="Dotations"
            value={dotationsLine}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
